import React, { useCallback, useEffect, useState, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { 
  Grid, 
  LinearProgress, 
  Typography, 
  Button, 
  Divider, 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip,
  TextField
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import officialCampaignService from "../../services/officialCampaignService";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ForbiddenPage from "../../components/ForbiddenPage";
import OfficialCampaignModal from "../../components/OfficialCampaignModal";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
  sentChip: {
    backgroundColor: theme.palette.info ? theme.palette.info.main : theme.palette.primary.main,
  },
  deliveredChip: {
    backgroundColor: theme.palette.success.main,
  },
  readChip: {
    backgroundColor: theme.palette.primary.main,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: theme.palette.error.main,
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  diagnosticPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.default,
  },
  diagnosticWarning: {
    borderLeftColor: theme.palette.warning.main,
    backgroundColor: theme.palette.warning.light,
  },
  diagnosticGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  errorBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: "#fff",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
}));

const toDateTimeLocalValue = (value) => {
  const date = value ? new Date(value) : new Date();
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const OfficialCampaignReport = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const canUseOfficialCampaign = !!user && (user.profile !== "user" || user.allowOfficialCampaign === "enabled");

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [draftScheduledAt, setDraftScheduledAt] = useState(toDateTimeLocalValue(Date.now() + 5 * 60 * 1000));
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  // Dados para o gráfico de pizza
  const [chartData, setChartData] = useState({
    labels: ['Aceitas pela Meta', 'Entregues', 'Lidas', 'Falhas'],
    datasets: [
      {
        data: [0, 0, 0, 0],
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });
  
  // Opções para o gráfico de pizza
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const fetchCampaign = useCallback(async () => {
    try {
      const { data } = await officialCampaignService.show(id);
      setCampaign(data);
    } catch (err) {
      toastError(err);
    }
  }, [id]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await officialCampaignService.stats(id);
      setStats(data);
      
      // Atualizar dados do gráfico
      setChartData({
        labels: ['Aceitas pela Meta', 'Entregues', 'Lidas', 'Falhas'],
        datasets: [
          {
            data: [data.sent || 0, data.delivered || 0, data.read || 0, data.failed || 0],
            backgroundColor: [
              'rgba(54, 162, 235, 0.7)',
              'rgba(75, 192, 192, 0.7)',
              'rgba(153, 102, 255, 0.7)',
              'rgba(255, 99, 132, 0.7)',
            ],
            borderColor: [
              'rgba(54, 162, 235, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 99, 132, 1)',
            ],
            borderWidth: 1,
          },
        ],
      });
    } catch (err) {
      toastError(err);
    }
  }, [id]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await officialCampaignService.contacts(id, {
        pageNumber: page + 1,
        pageSize: rowsPerPage
      });
      setContacts(data.records || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [id, page, rowsPerPage]);

  const refreshContacts = async () => {
    await Promise.all([fetchCampaign(), fetchStats(), fetchContacts()]);
  };

  const handleScheduleDraft = async () => {
    try {
      const scheduledAt = draftScheduledAt ? new Date(draftScheduledAt).toISOString() : null;
      if (!scheduledAt || Number.isNaN(new Date(scheduledAt).getTime())) {
        toast.error("Informe uma data e hora válidas para o agendamento.");
        return;
      }
      await officialCampaignService.schedule(id, { scheduledAt });
      toast.success("Campanha agendada com sucesso.");
      await refreshContacts();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDispatchNow = async () => {
    try {
      await officialCampaignService.schedule(id);
      toast.success("Campanha enviada para disparo imediato.");
      await refreshContacts();
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    fetchCampaign();
    fetchStats();
  }, [fetchCampaign, fetchStats]);

  useEffect(() => {
    if (!campaign) return;
    if (campaign.scheduledAt) {
      setDraftScheduledAt(toDateTimeLocalValue(campaign.scheduledAt));
      return;
    }
    if (campaign.status === "draft") {
      setDraftScheduledAt(toDateTimeLocalValue(Date.now() + 5 * 60 * 1000));
    }
  }, [campaign]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    const companyId = user?.companyId;
    if (!socket || !companyId) return;

    const handler = payload => {
      if (payload.record?.id === Number(id)) {
        setCampaign(payload.record);
        fetchStats();
        fetchContacts();
        return;
      }

      if (payload.campaignId === Number(id)) {
        fetchStats();
        fetchContacts();
      }
    };

    socket.on(`company-${companyId}-official-campaign`, handler);
    return () => socket.off(`company-${companyId}-official-campaign`, handler);
  }, [socket, user?.companyId, id, fetchStats, fetchContacts]);

  const formatStatus = (val) => {
    switch (val) {
      case "scheduled":
        return i18n.t("campaignReport.scheduled");
      case "running":
        return i18n.t("campaignReport.process");
      case "finished":
        return i18n.t("campaignReport.finished");
      case "paused":
        return "Pausada";
      case "draft":
        return "Rascunho";
      case "cancelled":
        return i18n.t("campaignReport.cancelled");
      default:
        return val;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "finished":
        return "primary";
      case "running":
        return "secondary";
      case "scheduled":
        return "default";
      case "paused":
        return "default";
      case "draft":
        return "default";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const getMessageStatusChip = (contact) => {
    const status = contact?.status;
    const errorMessage = contact?.errorMessage;

    if (contact?.readAt) {
      return (
        <Chip
          label="Lida"
          size="small"
          icon={<DoneAllIcon fontSize="small" />}
          className={`${classes.statusChip} ${classes.readChip}`}
        />
      );
    }

    if (contact?.deliveredAt) {
      return (
        <Chip
          label="Entregue"
          size="small"
          icon={<DoneAllIcon fontSize="small" />}
          className={`${classes.statusChip} ${classes.deliveredChip}`}
        />
      );
    }

    switch (status) {
      case 'sent':
        return (
          <Chip 
            label="Aceita pela Meta"
            size="small" 
            icon={<SendIcon fontSize="small" />}
            className={`${classes.statusChip} ${classes.sentChip}`}
          />
        );
      case 'pending':
      case 'queued':
        return (
          <Chip 
            label={i18n.t("campaignReport.pending")} 
            size="small" 
            icon={<ScheduleIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.pendingChip}`} 
          />
        );
      case 'failed':
        const chip = (
          <Chip 
            label={i18n.t("campaignReport.failed")} 
            size="small" 
            icon={<ErrorIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.failedChip}`} 
          />
        );
        // Se tem mensagem de erro, mostra em um tooltip
        if (errorMessage) {
          return (
            <Tooltip title={errorMessage} placement="top" arrow>
              {chip}
            </Tooltip>
          );
        }
        return chip;
      default:
        return (
          <Chip 
            label={status || i18n.t("campaignReport.unknown")} 
            size="small" 
            variant="outlined" 
          />
        );
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };

  const exportToExcel = async () => {
    try {
      toast.info("Gerando relatório Excel, aguarde...");
      
      let allContacts = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000;
      
      while (hasMoreData) {
        const { data } = await officialCampaignService.contacts(id, {
          pageNumber: currentPage,
          pageSize: pageSize
        });
        
        if (!data || !data.records || data.records.length === 0) {
          break;
        }
        
        allContacts = [...allContacts, ...data.records];
        hasMoreData = currentPage < (data.totalPages || 1);
        currentPage++;
        
        toast.info(`Carregando dados... ${allContacts.length} registros encontrados`);
      }
      
      if (allContacts.length === 0) {
        toast.error("Nenhum dado encontrado para exportar");
        return;
      }

      const excelData = [
        ["Relatório de Campanha Oficial", "", "", "", "", "", ""],
        [`Campanha: ${campaign?.name || 'N/A'}`, "", "", "", "", "", ""],
        [`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, "", "", "", "", "", ""],
        ["", "", "", "", "", "", ""],
        ["ID", "Destinatário", "Mensagem", "Enviado em", "Entregue em", "Lido em", "Status"],
      ];

      allContacts.forEach(contact => {
        const payload = typeof contact.payload === 'string' 
          ? JSON.parse(contact.payload) 
          : contact.payload;
        const messageText = payload?.components?.find(c => c.type === 'BODY')?.text || 
                           payload?.components?.find(c => c.type === 'body')?.text || 
                           'Template';
        
        const statusLabel = contact.status === 'failed' ? 'Falha' :
          contact.readAt ? 'Lida' :
          contact.deliveredAt ? 'Entregue' :
          contact.status === 'sent' ? 'Aceita pela Meta' :
          'Pendente';

        excelData.push([
          contact.id || '',
          contact.phoneNumber || '',
          messageText.substring(0, 100),
          contact.lastAttemptAt ? new Date(contact.lastAttemptAt).toLocaleString('pt-BR') : '',
          contact.deliveredAt ? new Date(contact.deliveredAt).toLocaleString('pt-BR') : '',
          contact.readAt ? new Date(contact.readAt).toLocaleString('pt-BR') : '',
          statusLabel
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 50 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 }
      ];

      ws['!merges'] = [
        { s: { c: 0, r: 0 }, e: { c: 6, r: 0 } },
        { s: { c: 0, r: 1 }, e: { c: 6, r: 1 } },
        { s: { c: 0, r: 2 }, e: { c: 6, r: 2 } }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Campanha");

      const fileName = `relatorio_campanha_oficial_${(campaign?.name || 'campanha').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
      
      toast.success(`Relatório Excel gerado com sucesso! ${allContacts.length} registros exportados.`);
      
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  const sent = stats?.sent || 0;
  const delivered = stats?.delivered || 0;
  const read = stats?.read || 0;
  const failed = stats?.failed || 0;
  const optedOut = stats?.optedOut || 0;
  const pending = stats?.pending || 0;
  const processed = stats?.processed ?? (sent + failed + optedOut);
  const totalContacts = stats?.totalContacts || campaign?.totalContacts || 0;
  const progressPercent = totalContacts > 0 ? Math.min((processed / totalContacts) * 100, 100) : 0;
  const errorRatePercent = Math.round((stats?.errorRate || 0) * 100);
  const optOutRatePercent = Math.round((stats?.optOutRate || 0) * 100);
  const errorGroups = stats?.errorGroups || [];
  const hasPendingContacts = pending > 0;
  const isPausedWithPending = campaign?.status === "paused" && hasPendingContacts;

  return !canUseOfficialCampaign ? (
    <ForbiddenPage />
  ) : (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/official-campaigns')}
              startIcon={<ArrowBackIcon />}
              >
              Voltar
            </Button>
            {campaign?.status === "draft" && (
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10, marginRight: 10 }}>
                <TextField
                  type="datetime-local"
                  size="small"
                  variant="outlined"
                  label="Agendar para"
                  InputLabelProps={{ shrink: true }}
                  value={draftScheduledAt}
                  onChange={(event) => setDraftScheduledAt(event.target.value)}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleDispatchNow}
                >
                  Disparar agora
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => setEditModalOpen(true)}
                >
                  Editar rascunho
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleScheduleDraft}
                >
                  Agendar campanha
                </Button>
              </Box>
            )}
            <Title>Relatório de {campaign?.name || "Campanha Oficial"}</Title>
          </Grid>
        </Grid>
      </MainHeader>
      
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {campaign?.name || "Campanha Oficial"}
          </Typography>
          <Chip 
            label={formatStatus(campaign?.status)} 
            color={getStatusColor(campaign?.status)}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
        </Box>
        
        <Grid container spacing={3} className={classes.summaryCards}>
          {/* Card de Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Status de Entrega
                </Typography>
                <Box className={classes.pieChartContainer}>
                  <Doughnut data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Cards com estatísticas principais */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {sent}
                </Typography>
                <Typography color="textSecondary">
                  Aceitas pela Meta
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {totalContacts}
                </Typography>
                <Typography color="textSecondary">
                  Destinatários Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <CheckCircleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Entregues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Barra de progresso */}
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">Progresso</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {processed} processados de {totalContacts} | {pending} pendentes | {sent} aceitas | {delivered} entregues | {read} lidas | {failed} falhas
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            style={{ height: 10, borderRadius: 5, margin: "8px 0" }}
            value={progressPercent}
          />
        </Box>
        
        {/* Informações adicionais da campanha */}
        <Paper
          variant="outlined"
          className={`${classes.diagnosticPanel} ${isPausedWithPending ? classes.diagnosticWarning : ""}`}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Diagnóstico do disparo
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {isPausedWithPending
              ? "A campanha está pausada com contatos pendentes. Confira o motivo registrado e reinicie para continuar o envio."
              : "Acompanhamento operacional da campanha com pendências, falhas e retorno da Meta."}
          </Typography>
          {campaign?.pausedReason && (
            <Box mt={1}>
              <Chip
                size="small"
                color="default"
                label={`Motivo da pausa: ${campaign.pausedReason}`}
              />
            </Box>
          )}
          <Box className={classes.diagnosticGrid}>
            <Chip size="small" label={`Total: ${totalContacts}`} />
            <Chip size="small" label={`Processados: ${processed}`} />
            <Chip size="small" label={`Pendentes: ${pending}`} />
            <Chip size="small" label={`Falhas: ${failed}`} />
            <Chip size="small" label={`Taxa de erro: ${errorRatePercent}%`} />
            <Chip size="small" label={`Opt-out: ${optedOut} (${optOutRatePercent}%)`} />
          </Box>
          {errorGroups.length > 0 && (
            <Box className={classes.errorBox}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Falhas agrupadas
              </Typography>
              {errorGroups.map((item, index) => (
                <Typography key={`${item.errorCode || "sem-codigo"}-${index}`} variant="body2" color="textSecondary">
                  {item.total} contato(s) | Código {item.errorCode || "sem código"} | {item.errorMessage}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>

        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 12 }}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Contatos enviados pela campanha
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Fotografia da Lista Meta utilizada no agendamento.
            </Typography>
          </Box>
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Variáveis</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell>{contact.contact?.name || "Sem nome"}</TableCell>
                      <TableCell>{contact.phoneNumber}</TableCell>
                      <TableCell>
                        {[contact.var1, contact.var2, contact.var3, contact.var4, contact.var5, contact.var6]
                          .filter(Boolean)
                          .map((value, index) => (
                            <Chip
                              key={`${contact.id}-var-${index}`}
                              size="small"
                              label={`v${index + 1}: ${value}`}
                              style={{ marginRight: 4, marginBottom: 4 }}
                            />
                          ))}
                        {[contact.var1, contact.var2, contact.var3, contact.var4, contact.var5, contact.var6].every(v => !v) && (
                          <Typography variant="caption" color="textSecondary">Sem variáveis</Typography>
                        )}
                      </TableCell>
                      <TableCell>{getMessageStatusChip(contact)}</TableCell>
                    </TableRow>
                  ))}
                  {contacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {loading ? "Carregando..." : "Nenhum contato encontrado"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        <Grid container spacing={3}>
          {campaign?.whatsappId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <WhatsAppIcon />
                    </Avatar>
                  }
                  title="Conexão WhatsApp"
                  subheader={campaign.whatsapp?.name || "Não especificado"}
                />
              </Card>
            </Grid>
          )}
          
          {campaign?.contactListId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title="Lista de Contatos"
                  subheader={campaign.contactList?.name || "Não especificado"}
                />
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title="Agendamento"
                subheader={campaign?.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "Não agendado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title="Conclusão"
                subheader={campaign?.completedAt ? datetimeToClient(campaign.completedAt) : "Não concluído"}
              />
            </Card>
          </Grid>
        </Grid>
        
        {/* Seção de detalhes de mensagens */}
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <MessageIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Detalhes das Mensagens
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              style={{
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
              }}
            >
              Exportar Excel
            </Button>
          </Box>
          <Divider />
          
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="tabela de mensagens">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Destinatário</TableCell>
                  <TableCell>Mensagem</TableCell>
                  <TableCell>Enviado em</TableCell>
                  <TableCell>Entregue em</TableCell>
                  <TableCell>Lido em</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => {
                  const payload = typeof contact.payload === 'string' 
                    ? JSON.parse(contact.payload) 
                    : contact.payload;
                  const messageText = payload?.components?.find(c => c.type === 'BODY' || c.type === 'body')?.text || 
                                     payload?.components?.find(c => c.type === 'BODY' || c.type === 'body')?.parameters?.[0]?.text ||
                                     'Template';
                  
                  return (
                    <TableRow key={contact.id}>
                      <TableCell>{contact.id}</TableCell>
                      <TableCell>
                        <Tooltip title={contact.phoneNumber}>
                          <span>{formatPhoneNumber(contact.phoneNumber)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={messageText}>
                          <span>{messageText.substring(0, 50)}{messageText.length > 50 ? '...' : ''}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {contact.lastAttemptAt ? datetimeToClient(contact.lastAttemptAt) : '-'}
                      </TableCell>
                      <TableCell>
                        {contact.deliveredAt ? datetimeToClient(contact.deliveredAt) : '-'}
                      </TableCell>
                      <TableCell>
                        {contact.readAt ? datetimeToClient(contact.readAt) : '-'}
                      </TableCell>
                      <TableCell>{getMessageStatusChip(contact)}</TableCell>
                    </TableRow>
                  );
                })}
                {contacts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {loading ? "Carregando..." : "Nenhuma mensagem encontrada"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalContacts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        </Box>
      </Paper>

      {editModalOpen && (
        <OfficialCampaignModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={refreshContacts}
          campaignId={Number(id)}
        />
      )}

    </MainContainer>
  );
};

export default OfficialCampaignReport;
